import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, UserPlus, Save, X } from "lucide-react";
import { toast } from "sonner";

export const EMPTY_PARTY = { customer_id: null, name: "", phone: "", gstin: "", address: "", ship_to: "", same_ship: false, state: "", email: "" };

export const partyFromCustomer = (c) => ({ customer_id: c.id, name: c.name || "", phone: c.phone || "", gstin: c.gstin || "", address: c.address || "",
  ship_to: c.shipping_address || "", same_ship: !!c.address && (c.shipping_address || "") === (c.address || ""), state: c.state || "", email: c.email || "" });

function PartySearch({ onPick }) {
  const [q, setQ] = useState(""); const [res, setRes] = useState([]); const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q.trim()) { setRes([]); return; }
    const t = setTimeout(() => { api.get("/customers", { params: { search: q.trim() } }).then((r) => setRes(r.data.slice(0, 8))).catch(() => setRes([])); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search Party (Name / Mobile / GSTIN)" className="pl-9" data-testid="di-party-search" />
      {open && res.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white rounded-lg border shadow-lg max-h-64 overflow-y-auto">
          {res.map((c) => (
            <button key={c.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(c); setQ(""); setOpen(false); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" data-testid={`di-party-option-${c.id}`}>
              <div className="font-medium">{c.name}</div><div className="text-xs text-slate-500">{[c.phone, c.gstin, c.address].filter(Boolean).join(" · ")}</div>
            </button>
          ))}
        </div>
      )}
      {open && q.trim() && !res.length && <div className="absolute z-30 mt-1 w-full bg-white rounded-lg border shadow px-3 py-2 text-xs text-slate-500">No party found — use Create Party</div>}
    </div>
  );
}

export function BillToCard({ party, setParty, canManage, show = { phone: true, address: true, gstin: true, ship_to: true } }) {
  const [mode, setMode] = useState("add");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setParty((p) => { const n = { ...p, [k]: v }; if (n.same_ship && (k === "address" || k === "same_ship")) n.ship_to = n.address; return n; });
  async function saveParty() {
    if (!party.name.trim()) { toast.error("Party name is required"); return; }
    setSaving(true);
    try {
      const body = { name: party.name.trim(), phone: party.phone, gstin: party.gstin, address: party.address, shipping_address: party.ship_to, state: party.state, email: party.email };
      const { data } = await api.post("/customers", body);
      setParty(partyFromCustomer(data)); setMode("add"); toast.success("Party saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  }
  const field = (k, label, extra = {}) => (
    <div className={extra.cls || ""}><Label className="text-sm">{label}{extra.req && <span className="text-red-500"> *</span>}</Label>
      <Input value={party[k]} onChange={(e) => set(k, extra.upper ? e.target.value.toUpperCase() : e.target.value)} disabled={extra.disabled} className="mt-1" data-testid={`di-party-${k}`} /></div>
  );
  return (
    <Card className="p-4 border-slate-200" data-testid="di-billto-card">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
        <span className="text-base font-bold text-[#1A2A5E]">Bill To</span>
        {canManage && <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" checked={mode === "add"} onChange={() => setMode("add")} data-testid="di-party-mode-add" />Add Party</label>
          <span className="text-slate-300">|</span>
          <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" checked={mode === "create"} onChange={() => setMode("create")} data-testid="di-party-mode-create" />Create Party</label>
        </div>}
        {party.customer_id && <span className="ml-auto text-xs bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5 flex items-center gap-1" data-testid="di-party-linked">Saved party<button onClick={() => setParty({ ...party, customer_id: null })} title="Unlink"><X className="h-3 w-3" /></button></span>}
      </div>
      {canManage && mode === "add" && (
        <div className="flex gap-2 mb-3">
          <PartySearch onPick={(c) => setParty(partyFromCustomer(c))} />
          <Button variant="outline" size="icon" onClick={() => setMode("create")} title="Create Party" data-testid="di-party-create-icon"><UserPlus className="h-4 w-4" /></Button>
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-3">
        {field("name", "Party Name", { req: true })}
        {show.phone && field("phone", "Contact No.")}
        {show.gstin && field("gstin", "GSTIN", { upper: true })}
      </div>
      {(show.address || show.ship_to) && <div className={`grid ${show.address && show.ship_to ? "sm:grid-cols-2" : ""} gap-3 mt-3`}>
        {show.address && field("address", "Billing Address")}
        {show.ship_to && <div>
          <div className="flex items-center justify-between"><Label className="text-sm">Shipping Address</Label>
            {show.address && <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={party.same_ship} onChange={(e) => set("same_ship", e.target.checked)} data-testid="di-party-same-ship" />Same as Billing</label>}</div>
          <Input value={party.ship_to} onChange={(e) => set("ship_to", e.target.value)} disabled={party.same_ship && show.address} className="mt-1" data-testid="di-party-ship_to" />
        </div>}
      </div>}
      {(mode === "create" || party.state || party.email) && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3">{field("state", "State")}{field("email", "Email")}</div>
      )}
      {canManage && mode === "create" && (
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={() => setMode("add")}>Cancel</Button>
          <Button size="sm" className="bg-[#1A2A5E]" onClick={saveParty} disabled={saving} data-testid="di-party-save"><Save className="h-4 w-4 mr-1" />{saving ? "Saving..." : "Save Party"}</Button>
        </div>
      )}
    </Card>
  );
}
